import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

import { NumericField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';

import {
  getMissingOptimizerPricesStatusForActiveComponent,
  getMissingPlatePriceRowsForActiveComponent,
} from '../../../../store/selectors/missingLumberPriceSelectors';
import {
  MissingPlatePriceRow,
  resetMissingPlatePricesToDefaults,
  setMissingPlatePrice,
} from '../../../../store/slices/optimizationSettingsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { missingDefaultPriceHighlightSx, MissingDefaultPriceWarning } from './MissingDefaultPriceWarning';

function isAtDefault(row: MissingPlatePriceRow): boolean {
  return row.price === row.defaultPricePerPound;
}

export function PlateFactors({ disabled }: { readonly disabled: boolean }) {
  const status = useAppSelector(getMissingOptimizerPricesStatusForActiveComponent);
  const missingPlatePrices = useAppSelector(getMissingPlatePriceRowsForActiveComponent);

  if (status === 'error') {
    return (
      <InlineNotification type="warning">
        Couldn&apos;t load your organization&apos;s price gaps. The optimizer will use Paragon&apos;s default prices for
        this run.
      </InlineNotification>
    );
  }

  if (status !== 'loaded') return null;

  return (
    <Stack spacing={1}>
      {missingPlatePrices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Every plate the optimizer might press for this truss is priced &mdash; nothing to enter.
        </Typography>
      ) : (
        <MissingPlatePrices rows={missingPlatePrices} disabled={disabled} />
      )}
    </Stack>
  );
}

function MissingPlatePrices({
  rows,
  disabled,
}: {
  readonly rows: readonly MissingPlatePriceRow[];
  readonly disabled: boolean;
}) {
  const dispatch = useAppDispatch();
  const anyEdited = rows.some((row) => !isAtDefault(row));

  return (
    <Stack spacing={1}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="subtitle1">Missing plate prices</Typography>
        {anyEdited && (
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() => dispatch(resetMissingPlatePricesToDefaults())}
          >
            Reset to defaults
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">
        Some plate types the optimizer could press for this truss aren&apos;t priced for your organization. Keep
        Paragon&apos;s default or enter your own for this run. Priced in dollars per pound.
      </Typography>

      {rows.map((row, index) => {
        const missingDefault = row.defaultPricePerPound == null;
        const showMissingDefaultWarning = missingDefault && row.price == null;
        const tag = isAtDefault(row) && !missingDefault ? 'default' : null;

        return (
          <Stack
            key={row.plateType}
            spacing={0.25}
            sx={showMissingDefaultWarning ? missingDefaultPriceHighlightSx : undefined}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body1" noWrap title={row.plateType}>
                  {row.plateType}
                </Typography>
                {tag != null && (
                  <Typography variant="body2" color="text.secondary">
                    {tag}
                  </Typography>
                )}
              </Box>
              <NumericField<true>
                value={row.price}
                canBeNull
                nullPlaceholder="—"
                onSubmit={(price) => {
                  dispatch(setMissingPlatePrice({ index, price }));
                }}
                min={0}
                numberOfDecimals={2}
                disabled={disabled}
                fullWidth={false}
                startAdornment="$"
                sx={{ width: 96 }}
                slotProps={{ htmlInput: { 'aria-label': `${row.plateType} price per pound` } }}
              />
            </Box>
            {showMissingDefaultWarning && <MissingDefaultPriceWarning />}
          </Stack>
        );
      })}
    </Stack>
  );
}
